import React, { useEffect, useState } from "react";
import { Popover } from "@headlessui/react";
import { Link, NavLink } from "react-router-dom";
import clsx from "clsx";
import apiClient from "../api/apiClient";
import { useTheme } from "../context/ThemeContext";

const ThemeIcon = ({ dark }) => dark ? (
  <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="1.6" aria-hidden="true">
    <circle cx="12" cy="12" r="3.5" /><path d="M12 2v2M12 20v2M4.93 4.93l1.42 1.42M17.65 17.65l1.42 1.42M2 12h2M20 12h2M4.93 19.07l1.42-1.42M17.65 6.35l1.42-1.42" />
  </svg>
) : (
  <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="1.6" aria-hidden="true">
    <path d="M20.5 15.2A8 8 0 0 1 8.8 3.5 8.6 8.6 0 1 0 20.5 15.2Z" />
  </svg>
);

const MenuIcon = ({ open }) => (
  <span className="relative block h-4 w-5" aria-hidden="true">
    {[0, 1, 2].map((line) => (
      <span key={line} className={clsx(
        "absolute left-0 h-px w-5 bg-current transition-all",
        line === 0 && (open ? "top-[7px] rotate-45" : "top-0"),
        line === 1 && (open ? "top-[7px] opacity-0" : "top-[7px]"),
        line === 2 && (open ? "top-[7px] -rotate-45" : "top-[14px]")
      )} />
    ))}
  </span>
);

const Header = () => {
  const { isDark, toggleTheme } = useTheme();
  const [projectsComingSoon, setProjectsComingSoon] = useState(true);

  useEffect(() => {
    let active = true;
    apiClient.getProjects()
      .then((content) => active && setProjectsComingSoon(content.comingSoon))
      .catch(() => {});
    return () => { active = false; };
  }, []);

  const items = [
    { label: "Resume", to: "/resume" },
    { label: "Projects", to: "/work", comingSoon: projectsComingSoon },
  ];

  const navItem = (item, close) => (
    <NavLink
      key={item.label}
      to={item.to}
      title={item.comingSoon ? "Coming soon" : undefined}
      onClick={(event) => {
        if (item.comingSoon) event.preventDefault();
        else close?.();
      }}
      className={({ isActive }) => clsx(
        "relative px-1 py-2 font-mono text-[10px] uppercase tracking-[0.14em] text-muted transition hover:text-textmain",
        isActive && !item.comingSoon && "text-textmain after:absolute after:inset-x-1 after:bottom-0 after:h-px after:bg-accent",
        item.comingSoon && "cursor-not-allowed opacity-50"
      )}
    >
      {item.label}
      {item.comingSoon && <span className="ml-1.5 text-[8px] text-accent">/ Soon</span>}
    </NavLink>
  );

  return (
    <header className="sticky top-0 z-40 border-b border-line bg-bg/90 backdrop-blur-xl">
      <Popover>
        {({ open, close }) => (
          <div className="container-shell relative">
            <div className="flex h-16 items-center justify-between">
              <Link to="/" className="flex items-center gap-3" aria-label="Baba Hadj Said home">
                <span className="font-display text-lg font-semibold tracking-[-0.04em] text-textmain">B·H·S</span>
                <span className="hidden h-4 w-px bg-line sm:block" />
                <span className="hidden font-mono text-[9px] uppercase tracking-[0.12em] text-muted sm:block">Robotics / Software</span>
              </Link>

              <nav className="hidden items-center gap-8 md:flex" aria-label="Primary navigation">
                {items.map((item) => navItem(item))}
              </nav>

              <div className="flex items-center gap-2">
                <span className="mr-2 hidden items-center gap-2 font-mono text-[9px] uppercase tracking-[0.1em] text-muted lg:flex">
                  <span className="h-1.5 w-1.5 rounded-full bg-accent" /> Available
                </span>
                <button type="button" onClick={toggleTheme} className="grid h-9 w-9 place-items-center border border-line text-muted transition hover:border-accent hover:text-accent" aria-label={`Switch to ${isDark ? "light" : "dark"} mode`}>
                  <ThemeIcon dark={isDark} />
                </button>
                <Popover.Button className="grid h-9 w-9 place-items-center border border-line text-textmain md:hidden" aria-label={open ? "Close menu" : "Open menu"}>
                  <MenuIcon open={open} />
                </Popover.Button>
              </div>
            </div>
            <Popover.Panel className="absolute inset-x-0 top-full z-50 border border-line bg-surfaceSolid p-5 shadow-soft md:hidden">
              <nav className="grid gap-3" aria-label="Mobile navigation">
                {items.map((item) => navItem(item, close))}
                <a href="/#contact" onClick={close} className="px-1 py-2 font-mono text-[10px] uppercase tracking-[0.14em] text-muted">Contact</a>
              </nav>
            </Popover.Panel>
          </div>
        )}
      </Popover>
    </header>
  );
};

export default Header;
